import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CardHeader, ResponsiveContext } from 'grommet';
import { CircleInformation } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError, ClientService, TUpdateClientRequest } from '/src/api';
import {
  Box,
  BreadcrumbNav,
  ClientCorporateInformation,
  ClientCountry,
  ClientGeneralInformation,
  CommissionsList,
  ContactsSelect,
  FacilityList,
  FormCard,
  FormPage,
  InfoBox,
  Line,
  RetireClientModal,
  StatementList,
  Tabs,
  Text,
  UserList,
} from '/src/components';
import { ClientStripeBankingInput, StripeEmailHandle } from '/src/components/users/ClientStripeDetails';
import { DocumentLibrary } from '/src/components/users/DocumentLibrary';
import { config } from '/src/config';
import { useClientStore, useGlobalStore, useUserStore } from '/src/context';
import { useProductTour } from '/src/hooks/useProductTour';
import { RoleTypes, TClientId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { ClientDataForm, CountryId, FormMetadata } from '/src/lib/types';
import { PageNotFound } from '/src/pages';
import { clientIsRetired, getFormErrors, getIsMobile, getPageTitle, getQuarterFromDate } from '/src/utils';
import { companyTour } from '/src/utils/productTours';

export const ClientDetailsPage = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const clientStore = useClientStore();
  const screenSize = useContext(ResponsiveContext);
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  /** Refs **/
  const defaultFormData = useRef<ClientDataForm>({
    name: '',
    website: '',
    business_id: '',
    start_date: '',
    end_date: '',
    first_active_reporting_quarter: '',
    jumpstart_amount: '',
    comments: '',
    hq_address_line1: '',
    hq_address_line2: '',
    hq_address_city: '',
    hq_address_region_id: NaN,
    country_id: NaN,
    hq_address_post_code: '',
    programs: [],
    sales: [],
    referral: [],
  });

  /** State **/
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState(getInitialActiveTab());
  const [focusedTab, setFocusedTab] = useState('');
  const stripeEmailUpdateRef = useRef<StripeEmailHandle | null>(null);
  const [formValues, setFormValues] = useState<ClientDataForm>(defaultFormData.current);
  const [originalFormValues, setOriginalFormValues] = useState<ClientDataForm>(defaultFormData.current);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isInvalidParams, setIsInvalidParams] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [stripeEmailHasChanges, setStripeEmailHasChanges] = useState(false);
  const [isRetired, setIsRetired] = useState(false);
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  /** Computed **/
  const clientId = parseInt(params.client_id ?? '');
  const isMobile = getIsMobile(screenSize);
  const isCanada = clientStore.selectedClient?.country_id === CountryId.Canada;

  const lastReportingPeriod = clientStore.selectedClient?.last_active_reporting_quarter
    ? isCanada
      ? clientStore.selectedClient?.last_active_reporting_quarter?.slice(0, 4)
      : getQuarterFromDate(dayjs(clientStore.selectedClient?.last_active_reporting_quarter).toDate())
    : undefined;

  const titleBar = useMemo(() => {
    const badges: ReactNode[] = [];
    if (lastReportingPeriod) {
      badges.push(
        <InfoBox key="retired" pad={{ horizontal: '0.5rem' }} gap="xsmall">
          <Text size="0.95rem" weight={600} toUpperCase>
            {`${t('client:retired')}: ${lastReportingPeriod}`}
          </Text>
        </InfoBox>
      );
    }
    if (clientStore.selectedClient?.is_demo) {
      badges.push(
        <InfoBox
          key="demo"
          pad={{ horizontal: '0.5rem' }}
          gap="xsmall"
          background={{ color: 'status-critical', opacity: 'medium' }}
          border={{ color: 'status-critical' }}
        >
          <Text size="0.95rem" weight={600} toUpperCase>
            Demo Client
          </Text>
        </InfoBox>
      );
    }

    return (
      <Box gap="small" direction="row">
        {badges}
      </Box>
    );
  }, [lastReportingPeriod, t, clientStore.selectedClient?.is_demo]);

  const userCanRetire = userStore.isFuseAdmin && !isRetired;

  const menuButtonItems = useMemo(
    () => (userCanRetire ? [{ label: t('client:retireClient'), onClick: () => setShowRetireModal(true) }] : []),
    [userCanRetire, t]
  );

  /** Methods **/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateFormValue = (key: keyof ClientDataForm, value: any) => {
    setFormValues((prev) => {
      const newFormValues = {
        ...prev,
        [key]: value,
      };

      const changesExist = Object.keys(newFormValues).some(
        (k) =>
          JSON.stringify(newFormValues[k as keyof ClientDataForm]) !==
          JSON.stringify(originalFormValues[k as keyof ClientDataForm])
      );

      setHasChanges(changesExist);

      return newFormValues;
    });

    if (formErrors[key]) {
      setFormErrors((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
  };

  const fetchPageData = async (clientId: TClientId) => {
    try {
      setIsLoading(true);

      const client = await ClientService.get({ id: clientId });
      clientStore.setSelectedClient(client);

      document.title = getPageTitle(config.client.basePageTitle, client.name);

      setIsRetired(clientIsRetired(client));

      const formData = {
        ...formValues,
        name: client.name,
        first_active_reporting_quarter: client.first_active_reporting_quarter,
        start_date: client.start_date,
        end_date: client.end_date ?? '',
        website: client.website ?? '',
        business_id: client.business_id ?? '',
        jumpstart_amount: '',
        comments: client.comments,
        hq_address_line1: client.hq_address_line1 ?? '',
        hq_address_line2: client.hq_address_line2 ?? '',
        hq_address_city: client.hq_address_city ?? '',
        hq_address_region_id: client.hq_address_region?.id ?? NaN,
        country_id: client.country_id ?? NaN,
        hq_address_post_code: client.hq_address_post_code ?? '',
      };

      setFormValues(formData);
      setOriginalFormValues({ ...formData });
      setHasChanges(false);
      setStripeEmailHasChanges(false);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.fetchClient.error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const formFields: FormMetadata = {
      name: { label: 'Name', max: 100, required: true },
      start_date: { label: 'Start date', required: true },
      website: { label: 'Website', max: 255 },
      business_id: { label: 'FEIN or Business Number', required: true, max: 20 },
      comments: { label: 'Comments', max: 500 },
      hq_address_line1: { label: 'Address', max: 100, required: true },
      hq_address_line2: { label: 'Address', max: 100 },
      hq_address_city: { label: 'City', max: 50, required: true },
      hq_address_region_id: { label: 'Region', required: true },
      hq_address_post_code: { label: 'Postal code', max: 20, required: true },
    };

    const errors = getFormErrors(formFields, formValues);
    if (errors) setFormErrors(errors);
    return !errors;
  };

  const updateClient = async () => {
    const stripeEmailHandle = activeTab === 3 && stripeEmailHasChanges ? stripeEmailUpdateRef.current : null;
    if (stripeEmailHandle && !stripeEmailHandle.validate()) return;

    if (stripeEmailHandle && !hasChanges) {
      setIsUpdating(true);
      try {
        await stripeEmailHandle.save();
        setStripeEmailHasChanges(false);
        toast.success('Email updated successfully');
      } catch (err) {
        globalStore.handleApiError(err as ApiError, () => 'Failed to update email');
      } finally {
        setIsUpdating(false);
      }
      return;
    }

    if (!validateForm()) return;

    const clientWebsite = formValues.website
      ? formValues.website.split('://').length === 1
        ? `https://${formValues.website}`
        : formValues.website
      : null;

    const updatedClient: TUpdateClientRequest = {
      id: clientId,
      name: formValues.name,
      website: clientWebsite,
      business_id: formValues.business_id,
      jumpstart_amount: null,
      comments: formValues.comments || null,
      hq_address_line1: formValues.hq_address_line1,
      hq_address_line2: formValues.hq_address_line2 || null,
      hq_address_city: formValues.hq_address_city,
      hq_address_region_id: formValues.hq_address_region_id,
      hq_address_post_code: formValues.hq_address_post_code,
    };

    try {
      setIsUpdating(true);
      const client = await ClientService.update(updatedClient);
      clientStore.setSelectedClient(client);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setFormErrors({});

      if (stripeEmailHandle) {
        await stripeEmailHandle.save();
        setStripeEmailHasChanges(false);
      }

      const updatedFormValues = {
        ...formValues,
      };

      setFormValues(updatedFormValues);
      setOriginalFormValues({ ...updatedFormValues });
      setHasChanges(false);

      toast.success(toastMessages.updateClient.success);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.updateClient.error);
    } finally {
      setIsUpdating(false);
    }
  };

  function getInitialActiveTab() {
    const view = searchParams.get('view');
    if (view === 'contacts') return 1;
    if (view === 'documents') return 2;
    if (view === 'stripe') return 3;
    return 0;
  }

  /** Effects **/
  useEffect(() => {
    if (!clientId || globalStore.isLoading) return;
    fetchPageData(clientId);
  }, [clientId, globalStore.isLoading]);

  useEffect(() => {
    if (!clientId || globalStore.isLoading || !shouldRefresh) return;
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    queryClient.invalidateQueries({ queryKey: ['facilities'] });
    queryClient.invalidateQueries({ queryKey: ['equipment'] });
    fetchPageData(clientId).finally(() => setShouldRefresh(false));
  }, [clientId, globalStore.isLoading, shouldRefresh]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const view = searchParams.get('view');

    if (view === 'contacts' && activeTab !== 1) {
      setActiveTab(1);
    } else if (view === 'documents' && activeTab !== 2) {
      setActiveTab(2);
    } else if (view === 'stripe' && activeTab !== 3) {
      setActiveTab(3);
    } else if (!view && activeTab !== 0) {
      setActiveTab(0);
    }
  }, [location.search, activeTab]);

  //404 Redirect
  useEffect(() => {
    if (isLoading) return;
    setIsInvalidParams(Number.isNaN(clientId) || clientStore.selectedClient?.id === undefined);
  }, [clientId, clientStore.selectedClient, isLoading]);

  /* Product Tour */
  useProductTour({
    tourName: 'company-tour',
    steps: companyTour,
    autoStart: true,
    showOnce: true,
  });

  /** Render **/
  return isInvalidParams ? (
    <PageNotFound />
  ) : (
    <FormPage
      title={clientStore.selectedClient?.name}
      titleBar={titleBar}
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={[
            {
              name: t('navigation:clients'),
              link: !userStore.isClientUser && !userStore.isFacilityUser ? '/clients' : undefined,
            },
          ]}
          currentPageName={clientStore.selectedClient?.name ?? ''}
        />
      }
      isLoading={isLoading}
      isDetailPage
      isClientPage
      clientId={clientId}
      hideMenuButton={!isLoading && !menuButtonItems.length}
      menuButtonItems={menuButtonItems}
    >
      <Box gap="2rem">
        <FormCard
          title="Client Details"
          icon={<CircleInformation size="24px" color="brand" />}
          isLoading={isLoading || isUpdating}
          onSubmit={updateClient}
          hideSaveButton={
            activeTab === 1 ||
            activeTab === 2 ||
            (activeTab === 3 && !clientStore.selectedClient?.stripe_global_payouts_account_id)
          }
          hideRequiredText={activeTab === 1 || activeTab === 2 || activeTab === 3}
          saveButtonLabel={t('common:saveChanges')}
          saveButtonDisabled={!hasChanges && !stripeEmailHasChanges}
          header={
            <CardHeader pad="none">
              <Tabs
                titles={[t('client:details'), t('client:contacts'), t('client:documents'), t('client:stripeDetails')]}
                activeTab={activeTab}
                focusedTab={focusedTab}
                dataTours={['details-tab', 'contacts-tab', 'documents-tab', 'stripe-tab']}
                setActiveTab={(index) => {
                  if (activeTab === 3 && index !== 3) {
                    setStripeEmailHasChanges(false);
                  }

                  setActiveTab(index);
                  const newSearchParams = new URLSearchParams(location.search);

                  if (index === 1) {
                    newSearchParams.set('view', 'contacts');
                  } else if (index === 2) {
                    newSearchParams.set('view', 'documents');
                  } else if (index === 3) {
                    newSearchParams.set('view', 'stripe');
                  } else {
                    newSearchParams.delete('view');
                  }

                  navigate(`/clients/${clientId}?${newSearchParams.toString()}`, { replace: true });
                }}
                setFocusedTab={setFocusedTab}
              />
            </CardHeader>
          }
        >
          {activeTab === 0 && (
            <Box data-tour="company-info">
              <ClientGeneralInformation
                id="general_information"
                updateFormValue={updateFormValue}
                formValues={formValues}
                formErrors={formErrors}
                isCanada={isCanada}
                isRetired={!!lastReportingPeriod}
                retirementReasonText={clientStore.selectedClient?.retirement_reason}
                onSubmit={() => updateClient()}
                isUpdatePage
              />
            </Box>
          )}

          {activeTab === 0 && <Line margin="1rem" />}

          {activeTab === 0 && (
            <Box direction={isMobile ? 'column' : 'row'}>
              <Box id="corporate_information" width={isMobile ? '100%' : '50%'} data-tour="corporate-info">
                <ClientCorporateInformation
                  updateFormValue={updateFormValue}
                  formValues={formValues}
                  formErrors={formErrors}
                  onSubmit={() => updateClient()}
                />
              </Box>
              {isMobile ? <Line margin="0.5rem" /> : <Line direction="vertical" margin="2rem" />}
              <Box width={isMobile ? '100%' : '50%'}>
                <ClientCountry
                  updateFormValue={updateFormValue}
                  formValues={formValues}
                  formErrors={formErrors}
                  disabled
                />
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box data-tour="contacts" gap="none">
              <ContactsSelect
                id="contacts"
                contacts={(clientStore.selectedClient?.users ?? []).filter(
                  (user) => user.is_active && user.role.id === RoleTypes.ClientAdmin
                )}
                isCorporate
                isUpdatePage
              />
            </Box>
          )}

          {activeTab === 2 && (
            <Box data-tour="document-library">
              <DocumentLibrary clientId={clientId} />
            </Box>
          )}

          {activeTab === 3 && (
            <Box data-tour="remittance-details">
              <ClientStripeBankingInput
                clientId={clientId}
                onEmailChange={setStripeEmailHasChanges}
                emailUpdateRef={stripeEmailUpdateRef}
              />
            </Box>
          )}
        </FormCard>
        <FacilityList
          id="facility_list"
          title={t('common:facilities')}
          clientId={clientId}
          showAddButton
          showSearchInput
          showShareButton
          data-tour="facilities-list"
        />
        <Box data-tour="statements-list">
          <StatementList id="statement_list" title={t('statements:statements')} clientId={clientId} />
        </Box>
        {userStore.user && userStore.isInternalUser && (
          <Box data-tour="commission-rates">
            <CommissionsList
              id="commission_list"
              title={t('commissions:commissions')}
              clientId={clientId}
              clientPrograms={clientStore.selectedClient?.client_programs}
              salesCommissions={clientStore.selectedClient?.sales_commission_rates}
              referralCommissions={clientStore.selectedClient?.referral_commission_rates}
            />
          </Box>
        )}
        <Box data-tour="users-list">
          <UserList
            id="user_list"
            title={t('user:users')}
            mode="controlled"
            users={clientStore.selectedClient?.users}
            clientId={clientId}
            isClientPage
            showAddButton
          />
        </Box>
      </Box>
      {showRetireModal && clientStore.selectedClient && (
        <RetireClientModal
          client={clientStore.selectedClient}
          setIsVisible={setShowRetireModal}
          setShouldRefresh={setShouldRefresh}
          isCanada={isCanada}
        />
      )}
    </FormPage>
  );
});
